import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { MemberItem } from "@components/ui/blocks/MemberAssignmentBlock";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useState, useRef, useCallback } from "react";
import { showNotification } from "context/notification.service";
import BaseModal from "@components/modals/BaseModal";
import { ChangeUserPhotoModal } from "@components/modals/ChangeUserPhotoModal";
import { usersApi } from "@services/api/users";

const colorPalette = [
  "#A5B4FC", "#818CF8", "#C4B5FD", "#A78BFA", "#F9A8D4",
  "#FDA4AF", "#FCA5A5", "#FDBA74", "#FCD34D", "#FDE047",
  "#BEF264", "#86EFAC", "#6EE7B7", "#5EEAD4", "#67E8F9",
  "#7DD3FC", "#93C5FD", "#CBD5E1", "#94A3B8", "#D6D3D1",
  "#A8A29E",
];

export const Account = () => {
  const userData = useDataStore((state) => state.userData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const fetchUserData = useDataStore((state) => state.fetchUserData);

  const [displayName, setDisplayName] = useState(userData?.infos.display_name || "");
  const [fullName, setFullName] = useState(userData?.infos.full_name || "");
  const [phoneNumber, setPhoneNumber] = useState(userData?.infos.phone_number || "");
  const [colorTag, setColorTag] = useState(userData?.customization.color_tag || "#3B82F6");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const signatureFileInputRef = useRef<HTMLInputElement>(null);

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userData) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({ type: "error", message: "Format invalide. Acceptés : JPG, PNG, WEBP" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La taille dépasse 5MB" });
      return;
    }

    setIsUploadingSignature(true);
    try {
      await usersApi.uploadSignature(userData.id, file);
      await fetchUserData(userData.id);
      showNotification({ type: "success", message: "Signature enregistrée avec succès" });
    } catch {
      showNotification({ type: "error", message: "Échec du téléchargement de la signature" });
    } finally {
      setIsUploadingSignature(false);
      if (signatureFileInputRef.current) signatureFileInputRef.current.value = "";
    }
  };

  const handleUpdateEmail = useCallback(async () => {
    if (!userData?.id || !newEmail.trim()) return;
    setIsUpdatingEmail(true);
    try {
      await usersApi.updateEmail(userData.id, newEmail.trim());
      await fetchUserData(userData.id);
      showNotification({
        type: "success",
        message: "Adresse e-mail mise à jour avec succès",
      });
      setShowEmailModal(false);
      setNewEmail("");
    } catch (error: any) {
      const detail = error?.response?.data?.detail || "Échec de la mise à jour de l'adresse e-mail";
      showNotification({ type: "error", message: detail });
    } finally {
      setIsUpdatingEmail(false);
    }
  }, [userData?.id, newEmail, fetchUserData]);

  const handlePasswordReset = useCallback(async () => {
    if (!userData?.id) return;
    setIsSendingPasswordReset(true);
    try {
      await usersApi.sendPasswordReset(userData.id);
      showNotification({
        type: "success",
        message: "Un e-mail de réinitialisation du mot de passe a été envoyé à votre adresse",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Échec de l'envoi de l'e-mail de réinitialisation",
      });
    } finally {
      setIsSendingPasswordReset(false);
    }
  }, [userData?.id]);

  const handleSave = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!userData?.id) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateUserData(userData.id, {
        infos: {
          email: userData.infos.email,
          display_name: displayName,
          full_name: fullName,
          phone_number: phoneNumber,
        },
        customization: {
          ...userData.customization,
          color_tag: colorTag,
        },
      });
      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
    } catch (error: any) {
      console.error("Failed to update user:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-fit w-full max-w-500 pb-96 mt-10 mx-4 lg:ml-10">
      {showPhotoModal && (
        <ChangeUserPhotoModal onClose={() => setShowPhotoModal(false)} />
      )}
      {showEmailModal && (
        <BaseModal
          title="Modifier l'adresse e-mail"
          startIcon={<Icon1 name="email" size={20} />}
          onClose={() => { setShowEmailModal(false); setNewEmail(""); }}
          width="max-w-400"
        >
          <div className="flex flex-col gap-4 p-4">
            <div className="text-h4 text-neutral-500">
              Adresse actuelle : <span className="font-medium text-black">{userData?.infos.email}</span>
            </div>
            <TextField
              label="Nouvelle adresse e-mail"
              value={newEmail}
              onChange={(v) => setNewEmail(v)}
              placeholder="nouvelle@email.com"
              type="email"
            />
            <div className="flex flex-row justify-end gap-2">
              <SecondaryButton
                label="Annuler"
                variant="small"
                onClick={() => { setShowEmailModal(false); setNewEmail(""); }}
              />
              <Button
                label={isUpdatingEmail ? "Mise à jour..." : "Confirmer"}
                variant="small"
                onClick={handleUpdateEmail}
                disabled={isUpdatingEmail || !newEmail.trim()}
              />
            </div>
          </div>
        </BaseModal>
      )}
      <div className="flex flex-col  gap-4  ">
        <div className="flex flex-col   ">
          <Text1>Compte</Text1>
          <Text2>Configurez votre compte ici.</Text2>
          <Text3>
            Gérez vos informations personnelles, vos préférences et les
            paramètres de votre compte.
          </Text3>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Photo de profil</div>
          <UserAvatar
            src={userData?.customization.photo_url}
            className="w-24 h-24 rounded-full border flex-shrink-0"
            value={userData?.customization.avatar_seed || userData?.infos.display_name}
            size={96}
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18}></Icon1>}
            label="Ajouter une photo"
            variant="large"
            onClick={() => setShowPhotoModal(true)}
          ></SecondaryButton>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Signature personnelle</div>
          <div className="text-h5 text-neutral-400">
            Votre signature sera automatiquement appliquée lors de la génération de comptes-rendus.
          </div>
          {userData?.infos.signature_id ? (
            <div className="border rounded-xl p-3 bg-neutral-50">
              <img
                src={userData.infos.signature_id}
                alt="Votre signature"
                className="max-h-24 object-contain"
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-neutral-200 rounded-xl p-6 flex items-center justify-center">
              <span className="text-h4 text-neutral-400">Aucune signature enregistrée</span>
            </div>
          )}
          <input
            ref={signatureFileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleSignatureUpload}
            className="hidden"
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18} />}
            label={
              isUploadingSignature
                ? "Téléchargement..."
                : userData?.infos.signature_id
                ? "Changer la signature"
                : "Ajouter une signature"
            }
            variant="large"
            disabled={isUploadingSignature}
            onClick={() => signatureFileInputRef.current?.click()}
          />
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="Pseudo/Prénom (court)"
            value={displayName}
            onChange={(value) => setDisplayName(value)}
            placeholder="Marie C."
          />
          <TextField
            label="Nom complet"
            value={fullName}
            onChange={(value) => setFullName(value)}
            placeholder="Marie Curie"
          />
          <TextField
            label="Adresse e-mail"
            placeholder="marie.curie@life.com"
            value={userData?.infos.email}
            disabled={true}
          />
          <TextField
            label="Numéro de téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
          />
          <TextField
            label="UID"
            placeholder="uid_1234567890"
            value={userData?.id}
            disabled={true}
          />
          <div className="text-h4 text-neutral-400 mt-4">Drapeau personnel</div>
          <MemberItem
            memberName={userData?.infos.display_name || "Utilisateur"}
            memberColor={colorTag}
          ></MemberItem>
          <SecondaryButton
            startIcon={<Icon1 name="pinceau" size={18}></Icon1>}
            label="Changer de couleur"
            variant="large"
            onClick={() => setShowColorPicker(!showColorPicker)}
          ></SecondaryButton>
          {showColorPicker && (
            <div className="flex flex-wrap gap-1 mt-2">
              {colorPalette.map((color) => (
                <button
                  key={color}
                  onClick={() => setColorTag(color)}
                  className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                    colorTag === color
                      ? "ring-2 ring-offset-1 ring-black"
                      : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          )}
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            onClick={handleSave}
            disabled={isSaving}
          ></Button>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <SecondaryButton
            label="Modifier l'adresse email"
            onClick={() => setShowEmailModal(true)}
          />
          <SecondaryButton
            label={isSendingPasswordReset ? "Envoi en cours..." : "Réinitialiser le mot de passe (par e-mail)"}
            onClick={handlePasswordReset}
            disabled={isSendingPasswordReset}
          />
          <SecondaryButton
            danger={true}
            label="Supprimer le compte"
          ></SecondaryButton>
        </div>
      </div>
    </div>
  );
};
